import React from 'react';
import { MapPin, Phone, Mail, Clock, Instagram } from 'lucide-react';
import { businessInfo } from '../shopData';
import Ornament from './Ornament';

const Footer = () => {
  return (
    <footer className="bg-black text-white footer-lux" style={{ borderTop: '1px solid rgba(212, 175, 55, 0.15)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Top ornament */}
        <div style={{ marginBottom: '2rem' }}>
          <Ornament size={24} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Brand Section */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_kothari-jewels/artifacts/vies131i_logo.jpg"
                alt="Shri Kothari Jewellers"
                className="h-12 w-12 rounded-full object-cover"
                style={{ border: '1px solid rgba(212, 175, 55, 0.4)' }}
              />
              <span
                className="text-gold-primary"
                style={{ fontFamily: "'Cinzel', serif", fontSize: '1.05rem', letterSpacing: '0.05em', fontWeight: 500 }}
              >
                {businessInfo.name}
              </span>
            </div>
            <p className="footer-tagline mb-4">
              {businessInfo.tagline}
            </p>
            <a
              href={businessInfo.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-gold-primary hover:text-gold-secondary transition-colors"
              data-testid="footer-instagram-link"
              style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.88rem', letterSpacing: '0.05em' }}
            >
              <Instagram size={18} />
              <span>Follow us on Instagram</span>
            </a>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-gold-primary mb-5">Contact Us</h3>
            <div className="space-y-3" style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.9rem' }}>
              <div className="flex items-start space-x-3">
                <MapPin size={18} className="text-gold-primary mt-1 flex-shrink-0" />
                <span style={{ color: 'rgba(232, 230, 227, 0.75)' }}>{businessInfo.address}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone size={18} className="text-gold-primary flex-shrink-0" />
                <a
                  href={`tel:${businessInfo.phone}`}
                  className="hover:text-gold-primary transition-colors"
                  style={{ color: 'rgba(232, 230, 227, 0.75)' }}
                  data-testid="footer-phone-link"
                >
                  {businessInfo.phone}
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail size={18} className="text-gold-primary flex-shrink-0" />
                <a
                  href={`mailto:${businessInfo.email}`}
                  className="hover:text-gold-primary transition-colors"
                  style={{ color: 'rgba(232, 230, 227, 0.75)' }}
                  data-testid="footer-email-link"
                >
                  {businessInfo.email}
                </a>
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <div>
            <h3 className="text-gold-primary mb-5">Visit Our Store</h3>
            <div className="flex items-start space-x-3" style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.9rem' }}>
              <Clock size={18} className="text-gold-primary mt-1 flex-shrink-0" />
              <div style={{ color: 'rgba(232, 230, 227, 0.75)' }}>
                <p style={{ color: 'var(--gold-primary)', fontWeight: 500 }}>{businessInfo.hours.days}</p>
                <p>{businessInfo.hours.weekdays}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className="mt-12 pt-8"
          style={{ borderTop: '1px solid rgba(212, 175, 55, 0.12)' }}
        >
        </div>
      </div>

      {/* WhatsApp Float Button */}
      <a
        href={`https://wa.me/${businessInfo.whatsappNumber}`}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="Contact on WhatsApp"
        data-testid="whatsapp-float-btn"
      >
        <svg viewBox="0 0 32 32" className="whatsapp-icon">
          <path fill="currentColor" d="M16 0c-8.837 0-16 7.163-16 16 0 2.825 0.737 5.607 2.137 8.048l-2.137 7.952 7.933-2.127c2.42 1.37 5.173 2.127 8.067 2.127 8.837 0 16-7.163 16-16s-7.163-16-16-16zM16 29.467c-2.482 0-4.908-0.646-7.07-1.87l-0.507-0.292-5.247 1.407 1.407-5.247-0.292-0.507c-1.224-2.162-1.87-4.588-1.87-7.070 0-7.444 6.056-13.5 13.5-13.5s13.5 6.056 13.5 13.5c0 7.444-6.056 13.5-13.5 13.5zM21.803 19.208c-0.302-0.151-1.79-0.883-2.067-0.984-0.277-0.101-0.479-0.151-0.681 0.151s-0.783 0.984-0.96 1.186c-0.177 0.202-0.353 0.227-0.655 0.076s-1.279-0.472-2.436-1.503c-0.9-0.803-1.508-1.795-1.684-2.097s-0.019-0.465 0.133-0.615c0.136-0.136 0.302-0.353 0.453-0.529s0.202-0.302 0.302-0.504c0.101-0.202 0.050-0.378-0.025-0.529s-0.681-1.641-0.933-2.248c-0.245-0.592-0.494-0.512-0.681-0.521-0.176-0.008-0.378-0.010-0.58-0.010s-0.529 0.076-0.806 0.378c-0.277 0.302-1.058 1.034-1.058 2.521s1.083 2.925 1.234 3.127c0.151 0.202 2.132 3.256 5.164 4.565 0.722 0.311 1.286 0.497 1.725 0.636 0.726 0.231 1.387 0.198 1.91 0.120 0.582-0.087 1.79-0.732 2.042-1.439s0.252-1.312 0.177-1.439c-0.076-0.126-0.277-0.202-0.58-0.353z" />
        </svg>
      </a>
    </footer>
  );
};

export default Footer;
